import type {
	PartCodingApproval,
	PartCodingQueueLine
} from '../../models/part-coding.model';

export const ADMIN_EXPORT_HEADER = [
	'Line Id',
	'Batch',
	'Customer',
	'ISR',
	'Submitted',
	'Batch line',
	'Status',
	'Quantity',
	'Customer Description',
	'Part Code',
	'RK Description',
	'RK Inv',
	'MBA',
	'Source',
	'Conf',
	'Reason',
	'Approval'
] as const;

export interface UploadError {
	row: number;
	message: string;
}

export interface UploadResult {
	errors: UploadError[];
	decisions: Map<string, PartCodingApproval>;
}

export function formatSubmittedDate(timestamp: number): string {
	return new Date(timestamp).toLocaleDateString(undefined, {
		month: 'numeric',
		day: 'numeric',
		year: 'numeric'
	});
}

export function formatSubmittedTime(timestamp: number): string {
	return new Date(timestamp).toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit'
	});
}

export function formatSubmitted(timestamp: number): string {
	return `${formatSubmittedDate(timestamp)} ${formatSubmittedTime(timestamp)}`;
}

// Local calendar day as yyyy-mm-dd, used as the Date filter key.
export function submittedDay(timestamp: number): string {
	const date = new Date(timestamp);
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

export function submittedDayLabel(day: string): string {
	const [year, month, date] = day.split('-').map(Number);
	if (!year || !month || !date) return day;
	return new Date(year, month - 1, date).toLocaleDateString(undefined, {
		month: 'numeric',
		day: 'numeric',
		year: 'numeric'
	});
}

export function lineMatchesQuery(
	line: PartCodingQueueLine,
	query: string
): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return [
		line.customer,
		line.isr,
		line.mark,
		line.batchId,
		line.customerDescription,
		line.partCode,
		line.rkDescription,
		line.source,
		line.reason,
		formatSubmitted(line.submittedAt)
	]
		.join(' ')
		.toLowerCase()
		.includes(q);
}

function csvField(value: string | number | null): string {
	const text = String(value ?? '');
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function approvalLabel(approval: PartCodingApproval | null): string {
	if (approval === 'approved') return 'Approved';
	if (approval === 'rejected') return 'Rejected';
	return '';
}

export function buildAdminQueueCsv(
	lines: readonly PartCodingQueueLine[]
): string {
	const body = lines.map((line) =>
		[
			line.id,
			line.batchId ?? '',
			line.customer,
			line.isr,
			formatSubmitted(line.submittedAt),
			line.mark,
			line.status,
			line.quantity,
			line.customerDescription,
			line.status === 'RFQ' ? 'RFQ' : line.partCode,
			line.rkDescription,
			line.inventory,
			line.isMarketBasket === null
				? ''
				: line.isMarketBasket
					? 'Yes'
					: 'No',
			line.source,
			line.confidence + '%',
			line.reason,
			approvalLabel(line.approval)
		]
			.map(csvField)
			.join(',')
	);
	return [ADMIN_EXPORT_HEADER.map(csvField).join(','), ...body].join('\r\n');
}

// Minimal RFC 4180 reader: quoted fields, doubled quotes, embedded newlines.
export function parseCsv(text: string): string[][] {
	const rows: string[][] = [];
	let row: string[] = [];
	let field = '';
	let inQuotes = false;

	for (let i = 0; i < text.length; i++) {
		const char = text[i];
		if (inQuotes) {
			if (char === '"' && text[i + 1] === '"') {
				field += '"';
				i++;
			} else if (char === '"') {
				inQuotes = false;
			} else {
				field += char;
			}
		} else if (char === '"') {
			inQuotes = true;
		} else if (char === ',') {
			row.push(field);
			field = '';
		} else if (char === '\n' || char === '\r') {
			if (char === '\r' && text[i + 1] === '\n') i++;
			row.push(field);
			field = '';
			rows.push(row);
			row = [];
		} else {
			field += char;
		}
	}
	if (field !== '' || row.length) {
		row.push(field);
		rows.push(row);
	}
	return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
}

type ApprovalCell =
	| { valid: true; value: PartCodingApproval | null }
	| { valid: false };

function parseApprovalCell(raw: string): ApprovalCell {
	const text = raw.trim().toLowerCase();
	if (!text) return { valid: true, value: null };
	if (text === 'approved' || text === 'approve') return { valid: true, value: 'approved' };
	if (
		text === 'rejected' ||
		text === 'reject' ||
		text === 'disapproved' ||
		text === 'disapprove'
	) {
		return { valid: true, value: 'rejected' };
	}
	return { valid: false };
}

function columnIndex(header: readonly string[], name: string): number {
	return header.findIndex(
		(cell) => cell.trim().toLowerCase() === name.toLowerCase()
	);
}

// Reads an exported queue CSV back in. Approval is the only column applied;
// any problem fails the whole file so nothing is half-applied.
export function readApprovalUpload(
	text: string,
	queue: readonly PartCodingQueueLine[]
): UploadResult {
	const decisions = new Map<string, PartCodingApproval>();
	const errors: UploadError[] = [];

	const table = parseCsv(text);
	const header = table[0];
	if (table.length < 2 || !header) {
		return {
			decisions,
			errors: [{ row: 1, message: 'File has no data rows.' }]
		};
	}

	const idColumn = columnIndex(header, 'Line Id');
	const approvalColumn = columnIndex(header, 'Approval');
	if (idColumn < 0 || approvalColumn < 0) {
		return {
			decisions,
			errors: [
				{
					row: 1,
					message:
						'Missing required columns. Need “Line Id” and “Approval”.'
				}
			]
		};
	}

	const byId = new Map(queue.map((line) => [line.id, line]));
	const seen = new Set<string>();

	table.slice(1).forEach((cells, index) => {
		const row = index + 2;
		const id = (cells[idColumn] ?? '').trim();
		const raw = cells[approvalColumn] ?? '';

		if (!id) {
			errors.push({ row, message: 'Line Id is blank.' });
			return;
		}
		if (seen.has(id)) {
			errors.push({ row, message: `Duplicate Line Id “${id}”.` });
			return;
		}
		seen.add(id);

		const line = byId.get(id);
		if (!line) {
			errors.push({
				row,
				message: `Unknown Line Id “${id}” — not in the current queue.`
			});
			return;
		}

		const parsed = parseApprovalCell(raw);
		if (!parsed.valid) {
			errors.push({
				row,
				message:
					`Approval “${raw.trim()}” is not allowed. Use Approve, Disapprove, ` +
					`or leave blank. (${line.customer} mark ${line.mark})`
			});
			return;
		}
		if (!parsed.value || parsed.value === line.approval) return;
		if (line.approval) {
			errors.push({
				row,
				message:
					`Line is already ${approvalLabel(line.approval)} and can't be changed ` +
					`to ${approvalLabel(parsed.value)}. (${line.customer} mark ${line.mark})`
			});
			return;
		}
		decisions.set(id, parsed.value);
	});

	return {
		decisions: errors.length
			? new Map<string, PartCodingApproval>()
			: decisions,
		errors
	};
}
